import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ProductionInventoryService } from '../../services/production-inventory.service';
import { debounceTime, distinctUntilChanged, finalize, Subscription } from 'rxjs';
import { PaginatedResponse } from '../../../common/models/common.model';
import { WarehouseBatchStock } from '../../production-inventory.model';
import { SnackbarService } from '../../../services/snackbar.service';
import { SnackbarAlertStatus } from '../../../utils';
import { DatePipe } from '@angular/common';

interface StockRow {
  packaging: string;
  batch: string;
  mfg_date: string;
  exp_date: string;
  stock: string;
  boxes: string;
  warehouse_name: string;
}

interface GroupedInventory {
  product_name: string;
  rows: StockRow[];
}

@Component({
  selector: 'app-product-stock-list',
  standalone: false,
  templateUrl: './product-stock-list.component.html',
  styleUrl: './product-stock-list.component.scss',
  providers: [DatePipe],
})
export class ProductStockListComponent implements OnInit, OnDestroy {
  @Input() warehouseControl: FormControl = new FormControl();

  protected groupedInventory: GroupedInventory[] = [];
  protected isDataLoading: boolean = false;
  protected isExporting: boolean = false;
  protected searchControl = new FormControl('');

  protected totalCount: number = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected currentPage: number = 1;

  private subscriptions: Subscription = new Subscription();
  private dataSubscription!: Subscription;

  constructor(
    private productionInventoryService: ProductionInventoryService,
    private snackbarService: SnackbarService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.warehouseControl.valueChanges.subscribe(() => {
        this.currentPage = 1;
        this.loadInventory();
      })
    );

    this.subscriptions.add(
      this.searchControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
        this.currentPage = 1;
        this.loadInventory();
      })
    );

    this.loadInventory();
  }

  private loadInventory(): void {
    this.isDataLoading = true;

    const params: any = {
      page: this.currentPage,
    };

    const warehouseValue = this.warehouseControl.value;
    if (warehouseValue) {
      params.warehouse = warehouseValue;
    }

    const searchValue = this.searchControl.value?.trim();
    if (searchValue) {
      params.search = searchValue;
    }

    this.dataSubscription = this.productionInventoryService
      .getWarehouseBatchStocks(params)
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe((response: PaginatedResponse<WarehouseBatchStock>) => {
        this.totalCount = response.count;
        this.hasNextPage = !!response.next;
        this.hasPreviousPage = !!response.previous;
        this.groupedInventory = this.groupStock(response.results || []);
      });
  }

  private groupStock(items: WarehouseBatchStock[]): GroupedInventory[] {
    const grouped = new Map<string, StockRow[]>();

    for (const item of items) {
      const productName = item.product_name;

      const row: StockRow = {
        packaging: `${item.packing_size} ${item.unit}${item.packaging_type ? ' · ' + item.packaging_type : ''}`,
        batch: item.batch_number || '-',
        mfg_date: item.manufacturing_date
          ? this.datePipe.transform(item.manufacturing_date, 'dd MMM yyyy') || '-'
          : '-',
        exp_date: item.expiry_date ? this.datePipe.transform(item.expiry_date, 'dd MMM yyyy') || '-' : '-',
        stock: item.units_available || '0',
        boxes: item.boxes_available || '0',
        warehouse_name: item.warehouse_name || '-',
      };

      if (!grouped.has(productName)) {
        grouped.set(productName, []);
      }
      grouped.get(productName)!.push(row);
    }

    return Array.from(grouped.entries()).map(([product_name, rows]) => ({
      product_name,
      rows,
    }));
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.loadInventory();
  }

  protected exportReport(): void {
    this.isExporting = true;

    const warehouseValue = this.warehouseControl.value;
    const params = warehouseValue ? { warehouse: warehouseValue } : {};

    this.productionInventoryService
      .export_stock(params)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: response => {
          if (response?.report_url) {
            const a = document.createElement('a');
            a.href = response.report_url;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            this.snackbarService.show(
              response.message || 'Report downloaded successfully',
              SnackbarAlertStatus.SUCCESS
            );
          } else {
            this.snackbarService.show('Report URL not found in response', SnackbarAlertStatus.ERROR);
          }
        },
        error: err => {
          this.snackbarService.show(err.error?.message || 'Failed to generate report', SnackbarAlertStatus.ERROR);
        },
      });
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
  }
}
